import { NgIf, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { RootState } from '@next/core-lib/app';
import { ChipItem, AgrChipsFilterModule } from '@next/core-lib/components';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { CatalogItem } from 'src/app/models/catalog-item.model';

import { selectCatalogSubcategories, selectSelectedCatalogCategory } from '../../store/selectors';

@Component({
  selector: 'app-catalog-subcategories',
  templateUrl: './catalog-subcategories.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, AgrChipsFilterModule, MatButton, MatIcon, AsyncPipe, AgrI18nSharedModule],
})
export class CatalogSubCategoriesComponent implements OnInit, OnDestroy {
  @Output() categorySelected: EventEmitter<{
    category: CatalogItem;
    replaceLastBreadcrumb: boolean;
  }> = new EventEmitter();

  readonly destroy$ = new Subject<void>();

  selectedCatalogCategory$ = this.store.select(selectSelectedCatalogCategory);
  catalogSubcategories$ = this.store.select(selectCatalogSubcategories);
  allSubcategories$ = new Subject<ChipItem<CatalogItem>[]>();
  displayedSubcategories$ = new BehaviorSubject<ChipItem<CatalogItem>[]>([]);
  subCategoriesFormControl = new FormControl();

  shouldShowAllChips$: Observable<boolean>;
  shouldShowMoreButton$ = new Subject<boolean>();
  showMoreToggle$ = new BehaviorSubject<boolean>(false);
  private readonly maximumChipNumber = 3;
  private allSubcategories: ChipItem<CatalogItem>[] = [];

  constructor(
    private readonly store: Store<RootState>,
    @Inject(BreakpointServiceInjector) private readonly breakpointService: BreakpointService,
  ) {}

  ngOnInit(): void {
    combineLatest([this.catalogSubcategories$, this.selectedCatalogCategory$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([currentSubcategories, selectedCategory]) => {
        if (currentSubcategories?.length > 0) {
          if (!this.allSubcategories) {
            this.allSubcategories = [];
          }
          this.allSubcategories = currentSubcategories.map((subcategory) => ({
            name: subcategory.description,
            value: subcategory,
            selected: subcategory.entryId === selectedCategory?.entryId,
          }));

          this.allSubcategories$.next(this.allSubcategories);
        }
      });

    combineLatest([this.breakpointService.ltSm$, this.allSubcategories$, this.showMoreToggle$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([ltSm, subcategories, showMoreToggle]) => {
        const shouldShowButton = (Array.isArray(ltSm) ? ltSm[0] : ltSm) && subcategories.length > this.maximumChipNumber;
        this.shouldShowMoreButton$.next(shouldShowButton);

        if (shouldShowButton) {
          if (!showMoreToggle) {
            const currentSubcategories = this.allSubcategories.slice(0, this.maximumChipNumber);
            this.displayedSubcategories$.next(currentSubcategories);
          } else {
            this.displayedSubcategories$.next(this.allSubcategories);
          }
        } else {
          this.displayedSubcategories$.next(this.allSubcategories);
        }
      });
  }

  selectSubcategory(selectedSubcategory: CatalogItem | CatalogItem[]) {
    const newCategory = Array.isArray(selectedSubcategory) ? selectedSubcategory[0] : selectedSubcategory;

    if (newCategory) {
      combineLatest([this.displayedSubcategories$, this.selectedCatalogCategory$])
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe(([subCategories, selectedCategory]) => {
          if (selectedCategory !== newCategory) {
            this.categorySelected.emit({
              category: newCategory,
              replaceLastBreadcrumb: subCategories.some((subCategory) => subCategory.value.entryId === selectedCategory.entryId),
            });
          }
        });
    }
  }

  toggleShowMoreSubcategories() {
    this.showMoreToggle$.pipe(take(1), takeUntil(this.destroy$)).subscribe((showMore) => this.showMoreToggle$.next(!showMore));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
